import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory/quick-insert-provider';

/**
 * EDITOR-6558: Allowlist of `QuickInsertItem` IDs that are compatible with
 * Markdown Mode (i.e. their inserted node/mark survives a clean GFM
 * round-trip via `@atlassian/mdast-util-from-pm`).
 *
 * Used by Confluence's editor preset to filter the quick-insert (`/`) menu
 * when the editor is in Markdown Mode.
 *
 * ### Why an allowlist?
 *
 * An allowlist defaults to "hide unknowns", which is the safer behaviour as
 * new quick-insert items are added: they will be hidden from Markdown Mode
 * by default until explicitly opted in. Cross-reference each entry with
 * `@atlassian/mdast-util-from-pm`'s coverage in `__tests__/adf-coverage.test.ts`.
 *
 * ### Maintenance
 *
 * When a new mdast-from-pm handler ships, add the corresponding
 * `QuickInsertItemId` here. When you remove a handler, remove the
 * corresponding ID.
 */
export const MARKDOWN_COMPATIBLE_QUICK_INSERT_ITEM_IDS: readonly string[] = [
	// Hyperlinks → `[text](url)`
	'hyperlink',

	// Tables → GFM tables
	'table',

	// Headings → `#`/`##`/`###`/...
	'heading1',
	'heading2',
	'heading3',
	'heading4',
	'heading5',
	'heading6',

	// Code blocks → ` ``` `
	'codeblock',

	// Block quote → `>`
	'blockquote',

	// Lists → `-`/`*`/`1.` and task lists → `- [x]`
	'unorderedList',
	'orderedList',
	'action', // task / action items (GFM checkbox lists)

	// Horizontal rule → `---`
	'rule',

	// Media — EDITOR-6636: `mediaSingle` + `media` now round-trip cleanly
	// through the markdown pipeline.
	//   - `type: "external"` → `![alt](https://…)` verbatim.
	//   - `type: "file"`     → `![alt](/wiki/download/attachments/{contentId}/{name})`
	//                          (Confluence tenant proxy URL, auth via session cookie).
	//
	// NOTE: Confluence uses `editor-plugin-media-insert` (the newer flow),
	// which registers the quick-insert item with id `'media-insert'` — NOT
	// `'media'` (which is the legacy media plugin's id used when mediaInsert
	// is absent). Both are allowlisted here for compatibility.
	'media-insert',
	'media',
];

/** Convenience `Set` for O(1) lookups. */
export const MARKDOWN_COMPATIBLE_QUICK_INSERT_ITEM_IDS_SET: ReadonlySet<string> = new Set(
	MARKDOWN_COMPATIBLE_QUICK_INSERT_ITEM_IDS,
);

/**
 * Predicate suitable for passing as `QuickInsertOptions.itemFilter` when in
 * Markdown Mode. Returns `true` for items whose `id` is on the allowlist.
 *
 * Items WITHOUT an `id` are also hidden — provider-supplied items
 * (extensions, diagrams, databases, marketplace plugins, Forge apps, etc.)
 * are dynamically injected via `quickInsertProvider`, are unique to
 * Confluence, and don't survive a clean GFM round-trip. Hiding them by
 * default is safer than the alternative — if a future extension does
 * survive round-trip, the consumer can layer their own item filter that
 * explicitly allowlists it.
 */
export const isMarkdownCompatibleQuickInsertItem = (item: QuickInsertItem): boolean => {
	if (!item.id) {
		return false;
	}
	return MARKDOWN_COMPATIBLE_QUICK_INSERT_ITEM_IDS_SET.has(item.id);
};
